import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { Add24Regular, ArrowRight24Regular } from "@fluentui/react-icons";

import { useProposalApi } from "./useProposalApi";
import type { Proposal } from "./types";
import { STATUS_LABELS, STATUS_COLORS } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  tabs: {
    padding: `0 ${tokens.spacingHorizontalL}`,
  },
  tableWrap: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalM,
  },
  clickableRow: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalM,
  },
});

interface ProposalListProps {
  isAdmin: boolean;
  onNewProposal: () => void;
  onSelectProposal: (proposal: Proposal) => void;
}

type FilterTab = "my" | "pending";

export const ProposalList: React.FC<ProposalListProps> = ({
  isAdmin,
  onNewProposal,
  onSelectProposal,
}) => {
  const styles = useStyles();
  const proposalApi = useProposalApi();

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTab, setFilterTab] = useState<FilterTab>("my");

  const loadProposals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = filterTab === "pending" ? "submitted" : undefined;
      const items = await proposalApi.list(status);
      setProposals(items);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load proposals");
    } finally {
      setLoading(false);
    }
  }, [proposalApi, filterTab]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Text size={400} weight="semibold">
          Sales Proposals
        </Text>
        <Button
          appearance="primary"
          icon={<Add24Regular />}
          onClick={onNewProposal}
        >
          New Proposal
        </Button>
      </div>

      {isAdmin && (
        <div className={styles.tabs}>
          <TabList
            selectedValue={filterTab}
            onTabSelect={(_e: SelectTabEvent, d: SelectTabData) =>
              setFilterTab(d.value as FilterTab)
            }
          >
            <Tab value="my">My Proposals</Tab>
            <Tab value="pending">Pending Approval</Tab>
          </TabList>
        </div>
      )}

      {error && (
        <MessageBar intent="error" style={{ margin: tokens.spacingHorizontalM }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.tableWrap}>
        {loading ? (
          <Spinner label="Loading proposals…" />
        ) : proposals.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={300}>
              {filterTab === "pending"
                ? "No proposals pending approval."
                : "No proposals yet."}
            </Text>
            {filterTab === "my" && (
              <Button appearance="primary" icon={<Add24Regular />} onClick={onNewProposal}>
                Create your first proposal
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Proposal ID</TableHeaderCell>
                <TableHeaderCell>Agency</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Created</TableHeaderCell>
                <TableHeaderCell>Updated</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.map((p) => (
                <TableRow
                  key={p.id}
                  className={styles.clickableRow}
                  onClick={() => onSelectProposal(p)}
                >
                  <TableCell>
                    <Text weight="semibold">{p.model.proposal_id || p.id.slice(0, 8)}</Text>
                  </TableCell>
                  <TableCell>{p.model.agency_name}</TableCell>
                  <TableCell>
                    <Badge color={STATUS_COLORS[p.status]} appearance="filled">
                      {STATUS_LABELS[p.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(p.created_at)}</TableCell>
                  <TableCell>{formatDate(p.updated_at)}</TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      icon={<ArrowRight24Regular />}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectProposal(p);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
